import { useEffect, useState } from 'react';

export type InsightDetailModalBrand = {
  name: string;
  domain: string;
  faviconUrl: string;
};

export type InsightDetailModalItem = {
  key: string;
  eyebrow: string;
  title: string;
  opportunity: string;
  nextSteps: string[];
  promptTexts: string[];
  searchedAt?: string | null;
  models: Array<{
    name: string;
    short: string;
    icon: string;
  }>;
  brands?: InsightDetailModalBrand[];
};

const formatInsightDateTime = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));

export function InsightDetailModal({
  item,
  onClose,
}: {
  item: InsightDetailModalItem | null;
  onClose: () => void;
}) {
  const [renderedItem, setRenderedItem] = useState<InsightDetailModalItem | null>(item);
  const [isVisible, setIsVisible] = useState(Boolean(item));
  const [isAnimating, setIsAnimating] = useState(Boolean(item));

  useEffect(() => {
    let frameA = 0;
    let frameB = 0;
    let timer = 0;

    if (item) {
      setRenderedItem(item);
      setIsVisible(true);
      frameA = window.requestAnimationFrame(() => {
        frameB = window.requestAnimationFrame(() => setIsAnimating(true));
      });
    } else if (renderedItem) {
      setIsAnimating(false);
      timer = window.setTimeout(() => {
        setIsVisible(false);
        setRenderedItem(null);
      }, 120);
    }

    return () => {
      if (frameA) window.cancelAnimationFrame(frameA);
      if (frameB) window.cancelAnimationFrame(frameB);
      if (timer) window.clearTimeout(timer);
    };
  }, [item, renderedItem]);

  useEffect(() => {
    if (!isVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose]);

  if (!isVisible || !renderedItem) return null;

  return (
    <div className={`rename-modal is-visible search-detail-modal-shell ${isAnimating ? 'is-open' : 'is-closing'}`}>
      <div className="white-background search-detail-modal">
        <div className="modal-close" onClick={onClose} style={{ cursor: 'pointer' }}>
          <div className="rotated-text">+</div>
        </div>

        <div className="search-detail-header">
          <div className="search-detail-header-top">
            <div className="text-size-medium text-weight-semibold">Full insight</div>
            <div className="insights-status-pill is-recommended">{renderedItem.eyebrow}</div>
          </div>
          <div className="spacer-xxtiny"></div>
          <div className="text-size-regular text-weight-regular text-colour-dark-brown">{renderedItem.title}</div>
          <div className="search-detail-divider"></div>
          <div className="insight-detail-meta-layout">
            <div className="insight-detail-meta-row">
              {renderedItem.brands && renderedItem.brands.length > 0 ? (
                <div className="search-detail-support-item insight-detail-support-stack insight-detail-meta-column is-brands">
                  <span className="text-size-xsmall text-colour-alt-grey">Brands</span>
                  <div className="search-detail-brands">
                    {renderedItem.brands.map((brand) => (
                      <div key={`insight-${renderedItem.key}-${brand.domain}`} className="search-detail-brand-pill">
                        <div className="search-brand-badge-image" style={{ backgroundImage: `url(${brand.faviconUrl})` }} />
                        <span>{brand.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {renderedItem.models.length > 0 ? (
                <div className="search-detail-support-item insight-detail-support-stack insight-detail-meta-column is-platforms">
                  <span className="text-size-xsmall text-colour-alt-grey">AI platforms</span>
                  <div className="insight-detail-platform-list">
                    {renderedItem.models.map((model) => (
                      <div key={`${renderedItem.key}-${model.name}`} className="search-detail-brand-pill">
                        <div className="overview-model-badge">
                          {model.icon ? (
                            <img src={model.icon} alt={model.name} className="overview-model-badge-image" />
                          ) : (
                            model.short
                          )}
                        </div>
                        <span>{model.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {renderedItem.searchedAt ? (
                <div className="search-detail-support-item insight-detail-meta-column is-date-added">
                  <span className="text-size-xsmall text-colour-alt-grey">Date added</span>
                  <span className="text-size-xsmall text-colour-dark-brown">{formatInsightDateTime(renderedItem.searchedAt)}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="search-detail-divider"></div>
        </div>

        {renderedItem.promptTexts.length > 0 ? (
          <>
            <div className="insight-detail-prompt-shell">
              <div className="search-detail-support-item insight-detail-support-stack insight-detail-meta-column">
                <span className="text-size-xsmall text-colour-alt-grey">Prompt used</span>
                <div className="insight-detail-prompt-list">
                  {renderedItem.promptTexts.map((promptText, index) => (
                    <div key={`${renderedItem.key}-prompt-${index}`} className="insight-detail-prompt-pill">
                      <span className="text-size-xsmall text-colour-dark-brown">{promptText}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="search-detail-divider"></div>
          </>
        ) : null}

        <div className="search-detail-scroll">
          <div className="search-rendered-content-shell insight-detail-content-shell">
            <div className="insight-detail-section">
              <div className="text-size-small text-weight-semibold text-colour-dark-brown">Opportunity</div>
              <div className="spacer-xxtiny"></div>
              <div className="text-size-small text-colour-dark-brown">{renderedItem.opportunity}</div>
            </div>

            {renderedItem.nextSteps.length > 0 ? (
              <>
                <div className="search-render-divider"></div>
                <div className="insight-detail-section">
                  <div className="text-size-small text-weight-semibold text-colour-dark-brown">Next steps:</div>
                  <div className="spacer-xxtiny"></div>
                  <div className="insights-bullet-list">
                    {renderedItem.nextSteps.map((idea, index) => (
                      <div key={`${renderedItem.key}-idea-${index}`} className="insights-bullet-item">
                        <div className="insights-bullet-dot"></div>
                        <div className="text-size-small text-colour-dark-brown">{idea}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="close-layer" onClick={onClose}></div>
    </div>
  );
}
